import type { Unit } from "@/models/units.model"
import { languageParser } from "../language.mapper"

export class UnitsMapper {
  private static _mapSingleUnitToDto(item: Unit | null | undefined): Unit {
    // Validar que el ítem no sea nulo
    if (!item) {
      return {} as Unit
    }

    try {
      return {
        code: item.code ?? "",
        name: item.name ?? "",
        description: languageParser(item.description) || {}, // Use languageParser to ensure description is localized
        order: typeof item.order === "number" ? item.order : 0,
        status: item.status ?? "not_started", // Default to "not_started" if status is not provided
        metadata: item.metadata
          ? {
              discipline: item.metadata.discipline ?? "",
              gender: item.metadata.gender ?? "",
              sportEvent: item.metadata.sportEvent ?? "",
              category: item.metadata.category ?? "",
              phase: item.metadata.phase ?? "",
              unit: item.metadata.unit ?? "",
              phaseCode: item.metadata.phaseCode ?? "",
              unitCode: item.metadata.unitCode ?? "",
            }
          : {
              discipline: "",
              gender: "",
              sportEvent: "",
              category: "",
              phase: "",
              unit: "",
              phaseCode: "",
              unitCode: "",
            },
        alternativeDescription: item.alternativeDescription ?? {},
        hasMedals: item.medalConfig?.hasMedals || false,
        medalCodes: item.medalConfig?.medals.map(m => m.code) || [],
        medalQuantities: item.medalConfig?.medalQuantities || [],
        subUnits: item.subUnits ? item.subUnits : [],
        timegap: item.timegap ?? "",
        unitTypeCode: item.unitTypeCode ?? "",
        dateInfo: item.dateInfo
          ? {
              startDate: item.dateInfo.startDate ?? "",
              endDate: item.dateInfo.endDate ?? "",
            }
          : {
              startDate: "",
              endDate: "",
            },
        venue: item.venue
          ? {
              code: item.venue.code ?? "",
              description: languageParser(item.venue.description) ?? {},
              order: typeof item.venue.order === "number" ? item.venue.order : 0,
            }
          : undefined,
        location: item.location ?? "",
        staff: item.staff ?? [],
        createdAt: item.createdAt ?? "",
        updatedAt: item.updatedAt ?? "",
      }
    }
    catch (error) {
      console.error("Error al procesar unidad:", error)
      return {} as Unit
    }
  }

  // Overload signatures for strong typing
  static toDto(rawData: Unit): Unit
  static toDto(rawData: Unit[]): Unit[]
  // Implementation signature
  static toDto(rawData: Unit | Unit[] | null | undefined): Unit | Unit[] {
    // Handle null or undefined input.
    // If null/undefined, _mapSingleUnitToDto returns {} as Unit.
    // If an array is expected and null/undefined is passed, an empty array is more appropriate.
    if (rawData === null || rawData === undefined) {
      // This behavior depends on expectation. If an array was expected, [] is better.
      // If a single object was expected, {} as Unit (from _mapSingleUnitToDto) is fine.
      // For simplicity and consistency with _mapSingleUnitToDto handling null item:
      // if rawData is null/undefined, and it's not an array, it will be treated as a single null item.
      // If it was intended as a null/undefined array, Array.isArray will be false.
      // The original code returned [] for any null/undefined or non-array input.
      // To maintain that for null/undefined specifically when an array might be context:
      // However, with overloads, TS helps. If `toDto(null)` is called, it's ambiguous without more context or specific null overload.
      // Let's assume `rawData` is `Unit` or `Unit[]`. If it can be `null` or `undefined`, the caller should handle that or types should reflect it.
      // If `rawData` is `null` or `undefined`, `Array.isArray` is false.
      // It will fall into the `else` block and `_mapSingleUnitToDto` will handle it.
    }

    if (Array.isArray(rawData)) {
      return rawData.map(item => UnitsMapper._mapSingleUnitToDto(item))
    }
    else {
      // Handles a single Unit object, or null/undefined if passed directly
      return UnitsMapper._mapSingleUnitToDto(rawData)
    }
  }
}
